import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/20/solid';
import { generatePONumber } from '../utils/poUtils';
import POReceivingModal from '../components/POReceivingModal';
import PaymentReferenceModal from '../components/PaymentReferenceModal';
import PillSelect from '../components/PillSelect';
import { Modal, ModalFooter, ModalButton } from '../components/ui';

const STATUS_FILTER_OPTIONS = [
  { value: '',          label: 'All statuses' },
  { value: 'draft',     label: 'Draft' },
  { value: 'issued',    label: 'Sent' },
  { value: 'received',  label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ROW_STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'issued',    label: 'Sent' },
  { value: 'received',  label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_FILTER_OPTIONS = [
  { value: '',       label: 'All payments' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid',   label: 'Paid' },
];

const ROW_PAYMENT_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid',   label: 'Paid' },
];

const PAYMENT_PILL = {
  unpaid: 'bg-amber-50 text-amber-700 border-amber-300',
  paid:   'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const STATUS_PILL = {
  draft:               'bg-slate-100 text-slate-700 border-slate-300',
  issued:              'bg-sky-50 text-sky-700 border-sky-300',
  partially_received:  'bg-amber-50 text-amber-700 border-amber-300',
  received:            'bg-emerald-50 text-emerald-700 border-emerald-300',
  closed:              'bg-slate-200 text-slate-700 border-slate-300',
  cancelled:           'bg-rose-50 text-rose-700 border-rose-300',
};

const STATUS_ORDER = { draft: 0, issued: 1, partially_received: 2, received: 3, closed: 4 };
const isStatusRevert = (oldStatus, newStatus) => {
  if (oldStatus === 'cancelled' || newStatus === 'cancelled') return false;
  return (STATUS_ORDER[newStatus] ?? 0) < (STATUS_ORDER[oldStatus] ?? 0);
};
const STATUS_LABEL_FOR_PROMPT = {
  draft: 'Draft',
  issued: 'Sent',
  partially_received: 'Partial',
  received: 'Received',
  cancelled: 'Cancelled',
  closed: 'Closed',
};

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString() : '-');

export default function PurchaseOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  // Receiving modal state
  const [receivingPo, setReceivingPo] = useState(null);
  const [receivingLines, setReceivingLines] = useState([]);
  // Payment reference prompt state
  const [paymentPo, setPaymentPo] = useState(null);
  // Generic confirm dialog
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    document.title = 'Vendor POs';
  }, []);

  const fetchPOs = async () => {
    setLoading(true);
    const [posRes, linesRes] = await Promise.all([
      supabase
        .from('purchase_orders')
        .select(`
          id, po_number, po_date, status, total, payment_status, paid_at, issued_at, closed_at, supplier_id,
          suppliers(id, name),
          shops(id, name)
        `)
        .order('po_date', { ascending: false })
        .order('po_number', { ascending: false }),
      supabase
        .from('purchase_order_lines')
        .select('purchase_order_id, quantity, received_quantity'),
    ]);
    if (posRes.error || linesRes.error) {
      console.error(posRes.error || linesRes.error);
      toast.error('Failed to load purchase orders.');
      setLoading(false);
      return;
    }
    // Aggregate per-PO progress and any-receipts flag
    const progress = {};
    (linesRes.data || []).forEach((l) => {
      const k = l.purchase_order_id;
      if (!progress[k]) progress[k] = { totalLines: 0, completedLines: 0, anyReceived: false };
      progress[k].totalLines++;
      const ordered = Number(l.quantity || 0);
      const received = Number(l.received_quantity || 0);
      if (received > 0) progress[k].anyReceived = true;
      if (ordered > 0 && received >= ordered) progress[k].completedLines++;
    });
    const enriched = (posRes.data || []).map((po) => ({
      ...po,
      progress: progress[po.id] || { totalLines: 0, completedLines: 0, anyReceived: false },
    }));
    setPos(enriched);
    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchPOs();
  }, [user]);

  const filtered = useMemo(() => {
    return pos.filter((po) => {
      if (statusFilter && po.status !== statusFilter) return false;
      if (paymentFilter && (po.payment_status || 'unpaid') !== paymentFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (po.po_number || '').toLowerCase().includes(q) ||
        (po.suppliers?.name || '').toLowerCase().includes(q) ||
        (po.shops?.name || '').toLowerCase().includes(q)
      );
    });
  }, [pos, search, statusFilter, paymentFilter]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const suggested = await generatePONumber();
      const { data, error } = await supabase
        .from('purchase_orders')
        .insert({
          po_number: suggested,
          status: 'draft',
          created_by: user?.id || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      navigate(`/purchase-orders/${data.id}`);
    } catch (err) {
      console.error('Create PO failed:', err);
      toast.error(err.message || 'Failed to create PO.');
    } finally {
      setCreating(false);
    }
  };

  // ---------- inline quick updates ----------
  const persistRowStatus = async (po, newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === 'issued' && !po.issued_at) update.issued_at = new Date().toISOString();
      if ((newStatus === 'received' || newStatus === 'cancelled') && !po.closed_at) {
        update.closed_at = new Date().toISOString();
      }
      if (isStatusRevert(po.status, newStatus)) {
        if ((STATUS_ORDER[newStatus] ?? 0) < STATUS_ORDER.issued && po.issued_at) update.issued_at = null;
        if ((STATUS_ORDER[newStatus] ?? 0) < STATUS_ORDER.received && po.closed_at) update.closed_at = null;
      }
      const { error } = await supabase.from('purchase_orders').update(update).eq('id', po.id);
      if (error) throw error;
      setPos((list) => list.map((p) => (p.id === po.id ? { ...p, ...update } : p)));
      toast.success(`${po.po_number} → ${STATUS_LABEL_FOR_PROMPT[newStatus] || newStatus}`);
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error(err.message || 'Failed to update status.');
      await fetchPOs();
    }
  };

  const handleRowStatusChange = async (po, newStatus) => {
    if (newStatus === po.status) return;

    // Validate Send: needs vendor + ≥1 line. We need to check the lines from server.
    if (newStatus === 'issued') {
      if (!po.supplier_id) {
        toast.error(`${po.po_number}: pick a vendor before sending.`);
        return;
      }
      if (!po.progress?.totalLines) {
        toast.error(`${po.po_number}: add at least one line before sending.`);
        return;
      }
    }

    // Picking Received with outstanding lines → just open the receive flow.
    // Status will auto-flip to Received via the DB trigger once all lines are fully received.
    if (newStatus === 'received') {
      const total = po.progress?.totalLines || 0;
      const complete = po.progress?.completedLines || 0;
      if (total > 0 && complete < total) {
        openReceiving(po);
        return;
      }
    }

    // Confirm cancel if PO has receipts
    if (newStatus === 'cancelled' && po.progress?.anyReceived) {
      setConfirm({
        title: `Cancel ${po.po_number}?`,
        message: `This PO has received material on record. The material is in inventory; cancelling won't reverse those receipts. Continue?`,
        primary: { label: 'Cancel PO', danger: true, onClick: () => persistRowStatus(po, newStatus) },
      });
      return;
    }

    // Confirm revert
    if (isStatusRevert(po.status, newStatus)) {
      setConfirm({
        title: 'Revert status?',
        message: `${po.po_number}: move from ${STATUS_LABEL_FOR_PROMPT[po.status]} back to ${STATUS_LABEL_FOR_PROMPT[newStatus]}? Progression timestamps will be cleared.`,
        primary: { label: 'Revert', danger: true, onClick: () => persistRowStatus(po, newStatus) },
      });
      return;
    }

    await persistRowStatus(po, newStatus);
  };

  const persistRowPayment = async (po, newPayment, reference) => {
    try {
      const update = { payment_status: newPayment };
      if (newPayment === 'paid') {
        if (!po.paid_at) update.paid_at = new Date().toISOString();
        if (reference !== undefined) update.payment_reference = reference;
      }
      if (newPayment === 'unpaid' && po.paid_at) update.paid_at = null;
      const { error } = await supabase.from('purchase_orders').update(update).eq('id', po.id);
      if (error) throw error;
      setPos((list) => list.map((p) => (p.id === po.id ? { ...p, ...update } : p)));
      toast.success(`${po.po_number} → ${newPayment}`);
    } catch (err) {
      console.error('Payment update failed:', err);
      toast.error(err.message || 'Failed to update payment.');
      await fetchPOs();
    }
  };

  const handleRowPaymentChange = async (po, newPayment) => {
    if (newPayment === (po.payment_status || 'unpaid')) return;
    if (newPayment === 'paid') {
      setPaymentPo(po);
      return;
    }
    await persistRowPayment(po, newPayment);
  };

  const confirmRowPaymentPaid = async (reference) => {
    if (!paymentPo) return;
    await persistRowPayment(paymentPo, 'paid', reference);
    setPaymentPo(null);
  };

  // ---------- receive from list ----------
  const openReceiving = async (po) => {
    try {
      const { data, error } = await supabase
        .from('purchase_order_lines')
        .select('*')
        .eq('purchase_order_id', po.id)
        .order('line_number');
      if (error) throw error;
      setReceivingPo(po);
      setReceivingLines(data || []);
    } catch (err) {
      console.error('Failed to load lines:', err);
      toast.error('Failed to load lines.');
    }
  };

  const closeReceiving = () => {
    setReceivingPo(null);
    setReceivingLines([]);
  };

  const canReceive = (status) => status === 'issued' || status === 'partially_received';

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-slate-800">Vendor POs</h1>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400"
        >
          <PlusIcon className="w-4 h-4" />
          {creating ? 'Creating…' : 'New PO'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by PO #, vendor, shop…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          {STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          {PAYMENT_FILTER_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">PO #</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Vendor</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Shop</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">PO Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Received</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Payment</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-slate-600 uppercase tracking-wide">Total</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-400">
                  {pos.length === 0 ? (
                    <div className="space-y-2">
                      <p>No vendor POs yet.</p>
                      <button
                        onClick={handleCreate}
                        disabled={creating}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400"
                      >
                        <PlusIcon className="w-4 h-4" /> Create your first PO
                      </button>
                    </div>
                  ) : (
                    'No POs match your filters.'
                  )}
                </td>
              </tr>
            ) : (
              filtered.map((po) => (
                <tr key={po.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm">
                    <Link to={`/purchase-orders/${po.id}`} className="text-sky-700 hover:underline font-medium">
                      {po.po_number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700">{po.suppliers?.name || '-'}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">{po.shops?.name || '-'}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">{fmtDate(po.po_date)}</td>
                  <td className="px-3 py-2 text-sm">
                    <PillSelect
                      value={po.status || 'draft'}
                      onChange={(e) => handleRowStatusChange(po, e.target.value)}
                      options={
                        po.status === 'partially_received'
                          ? [{ value: 'partially_received', label: 'Partial (auto)', disabled: true }, ...ROW_STATUS_OPTIONS]
                          : ROW_STATUS_OPTIONS
                      }
                      pillClass={STATUS_PILL[po.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
                    />
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">
                    {po.progress?.totalLines > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className={po.progress.completedLines === po.progress.totalLines ? 'text-emerald-700' : po.progress.completedLines > 0 ? 'text-amber-700' : 'text-slate-500'}>
                          {po.progress.completedLines} / {po.progress.totalLines}
                        </span>
                        <div className="flex-1 max-w-[80px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={po.progress.completedLines === po.progress.totalLines ? 'h-full bg-emerald-500' : 'h-full bg-amber-400'}
                            style={{ width: `${Math.min(100, (po.progress.completedLines / po.progress.totalLines) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <PillSelect
                      value={po.payment_status || 'unpaid'}
                      onChange={(e) => handleRowPaymentChange(po, e.target.value)}
                      options={ROW_PAYMENT_OPTIONS}
                      pillClass={PAYMENT_PILL[po.payment_status] || PAYMENT_PILL.unpaid}
                    />
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700 text-right tabular-nums">{fmtMoney(po.total)}</td>
                  <td className="px-3 py-2 text-right">
                    {canReceive(po.status) && (
                      <button
                        onClick={() => openReceiving(po)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm hover:bg-emerald-100"
                        title="Receive against this PO"
                      >
                        <InboxArrowDownIcon className="w-3.5 h-3.5" /> Receive
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <POReceivingModal
        isOpen={!!receivingPo}
        onClose={closeReceiving}
        po={receivingPo}
        lines={receivingLines}
        onReceived={() => {
          fetchPOs();
          closeReceiving();
        }}
      />

      <PaymentReferenceModal
        isOpen={!!paymentPo}
        onClose={() => setPaymentPo(null)}
        poNumber={paymentPo?.po_number}
        defaultReference={paymentPo?.payment_reference}
        onConfirm={confirmRowPaymentPaid}
      />

      {/* Generic confirm dialog */}
      <Modal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title || ''}
        size="md"
      >
        <p className="text-sm text-slate-700">{confirm?.message}</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </ModalButton>
          {confirm?.secondary && (
            <ModalButton
              variant="secondary"
              onClick={() => { confirm.secondary.onClick(); setConfirm(null); }}
            >
              {confirm.secondary.label}
            </ModalButton>
          )}
          {confirm?.primary && (
            <ModalButton
              variant={confirm.primary.danger ? 'danger' : 'primary'}
              onClick={() => { confirm.primary.onClick(); setConfirm(null); }}
            >
              {confirm.primary.label}
            </ModalButton>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
